import { useCallback, useContext } from 'react';
import type React from 'react';
import ConditionalTile from '@/ConditionalTiles/ConditionalTile';
import SearchSource from '~/constants/SearchSource';
import AppliedFiltersContext from '~/contexts/AppliedFiltersContext';
import { useIsMobile } from '~/contexts/DeviceContext';
import { useFinanceInfo } from '~/contexts/FinanceAndTradeContext';
import SearchEventsDataContext from '~/contexts/SearchEventsDataContext';
import DollarBillsIcon from '~/icons/DollarBillsIcon';
import DollarHandIcon from '~/icons/DollarHandIcon';
import ClickableTile from '../ClickableTile';

type TileSelection = 'low' | 'zero';

type Tile = {
  tileName: string;
  heading1: string;
  iconComponent: React.ReactNode;
};

const tileMap = new Map<TileSelection, Tile>([
  [
    'zero',
    {
      tileName: 'ZeroDownTile',
      heading1: 'Zero Cash Down',
      iconComponent: <DollarBillsIcon />
    }
  ],
  [
    'low',
    {
      tileName: 'LowDownTile',
      heading1: 'Low Down Payments',
      iconComponent: <DollarHandIcon />
    }
  ]
]);

export default function DownPaymentTile() {
  const { setSearchSource } = useContext(SearchEventsDataContext);
  const { updateRangeFilter } = useContext(AppliedFiltersContext);
  const financeInfo = useFinanceInfo();
  const isMobile = useIsMobile();

  const tileToShow = financeInfo?.minDownPayment === 0 ? 'zero' : 'low';
  const { tileName, heading1, iconComponent } = tileMap.get(tileToShow) ?? {};

  const onClick = useCallback<React.MouseEventHandler>(
    ev => {
      ev.preventDefault();
      updateRangeFilter({
        filterKey: 'finance',
        value: {
          downPayment: financeInfo?.minDownPayment,
          monthlyPayment: financeInfo?.maxMonthlyPayment
        }
      });

      setSearchSource(SearchSource.Tile);
    },
    [
      financeInfo?.maxMonthlyPayment,
      financeInfo?.minDownPayment,
      setSearchSource,
      updateRangeFilter
    ]
  );

  return (
    <ClickableTile
      tileId={tileName?.toLowerCase()}
      name={tileName}
      smallerSize={isMobile}
      handleTileClick={onClick}
    >
      <ConditionalTile
        isMobile={isMobile}
        hasToolTip={false}
        svgIcon={iconComponent}
        hasButton
        data={{
          content: {
            heading1,
            subHeading: 'Down payments that fit your budget.'
          },
          button: 'SHOP NOW'
        }}
        primaryButton
        hasMobileSubText
      />
    </ClickableTile>
  );
}
